import React, { FC, useMemo, useState } from "react";

import { getLinkedFirm } from "../../sessionStorage/sessionStorage";
import {
  useCreateClientRequestMutation,
  useGetClientRequestRecipientsQuery,
  useGetClientRequestSuggestionQuery,
  useGetClientRequestTemplatesQuery,
  useSaveClientRequestTemplateMutation,
  useUpdateClientRequestMutation,
} from "../../slices/ClientRequestsSlice";
import {
  modalCancel,
  modalPrimary,
  PortalModal,
} from "../clientPortal/PortalModal";
import {
  ClientRequest,
  ReminderMode,
  RequestItemInput,
  RequestSendResult,
} from "./clientRequests.types";
import {
  ITEM_KINDS,
  itemsAsNew,
  MAX_ITEMS,
  moveItem,
  newItem,
  recipientsLine,
  REMINDER_MODES,
  requestFormProblem,
  requestPayload,
} from "./clientRequests.utils";

const input =
  "h-10 w-full rounded-[10px] border-[1.5px] border-[#DCE2EA] bg-white px-3 text-[13.5px] font-medium text-[#1F2433] outline-none focus:border-[#6B4EFF]";
const label = "text-[12.5px] font-bold text-[#3A4256]";
const smallBtn =
  "h-8 rounded-[8px] border border-[#DCE2EA] bg-white px-2.5 text-[12.5px] font-bold text-[#3A4256] hover:bg-[#F7F8FA] disabled:opacity-40";
const iconBtn =
  "flex h-8 w-8 items-center justify-center rounded-[8px] text-[14px] text-[#6B7185] hover:bg-[#F1F2F5] disabled:opacity-30";

export const serverText = (error: unknown, fallback: string) => {
  const data = (error as { data?: unknown })?.data;
  if (typeof data === "string" && data.trim() && data.length < 400) return data;
  return fallback;
};

/**
 * New / Edit request list window (owner's mock-up 2026-10-31): title, due date, items (File / Question /
 * Yes-No), reminders, options; Save as template, Save draft, Send to client.
 */
export const RequestListModal: FC<{
  workId: string;
  editing?: ClientRequest | null;
  onClose: () => void;
  onDone: (message: string, result?: RequestSendResult) => void;
}> = ({ workId, editing, onClose, onDone }) => {
  const orgId = getLinkedFirm()?.orgId || "";
  const sentAlready = editing?.status === "sent";
  const [title, setTitle] = useState(editing?.title ?? "");
  const [message, setMessage] = useState(editing?.message ?? "");
  const [dueDate, setDueDate] = useState(editing?.dueDate ?? "");
  const [items, setItems] = useState<RequestItemInput[]>(
    editing?.items.map((i) => ({
      id: i.id,
      kind: i.kind,
      label: i.label,
      fileIfYes: i.fileIfYes,
    })) ?? [newItem("file")],
  );
  const [reminderMode, setReminderMode] = useState<ReminderMode>(
    editing?.reminderMode ?? "gentle",
  );
  const [everyDays, setEveryDays] = useState(editing?.reminderEveryDays || 5);
  const [moveToReady, setMoveToReady] = useState(
    editing?.moveToReadyWhenDone ?? true,
  );
  const [repeat, setRepeat] = useState(editing?.repeatWithWork ?? false);
  const [error, setError] = useState<string | null>(null);
  const [templateName, setTemplateName] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const recipients = useGetClientRequestRecipientsQuery({ workId });
  const suggestion = useGetClientRequestSuggestionQuery(
    { workId },
    { skip: !!editing },
  );
  const templates = useGetClientRequestTemplatesQuery(
    { orgId },
    { skip: !orgId },
  );
  const [create, createState] = useCreateClientRequestMutation();
  const [update, updateState] = useUpdateClientRequestMutation();
  const [saveTemplate, templateState] = useSaveClientRequestTemplateMutation();
  const busy = createState.isLoading || updateState.isLoading;

  const form = {
    title,
    message,
    dueDate,
    items,
    reminderMode,
    reminderEveryDays: Number(everyDays),
    moveToReadyWhenDone: moveToReady,
    repeatWithWork: repeat,
  };
  const who = recipientsLine(recipients.data?.people ?? []);
  const recipientProblem = recipients.data?.problem;

  const setItem = (index: number, patch: Partial<RequestItemInput>) =>
    setItems((list) =>
      list.map((it, i) => (i === index ? { ...it, ...patch } : it)),
    );

  const fill = (from: RequestItemInput[], text: string) => {
    const blank = items.every((i) => !i.label.trim());
    setItems(
      blank
        ? itemsAsNew(from)
        : [...items, ...itemsAsNew(from)].slice(0, MAX_ITEMS),
    );
    setNotice(text);
  };

  const submit = async (send: boolean) => {
    const problem =
      requestFormProblem(form, send || sentAlready) ||
      (send && recipientProblem ? recipientProblem : null);
    if (problem) return setError(problem);
    setError(null);
    const body = requestPayload(workId, form, send);
    const result = editing
      ? await update({ id: editing.id, body })
      : await create(body);
    if ("error" in result)
      return setError(
        serverText(result.error, "The request list couldn't be saved."),
      );
    const r = result.data;
    if (!send) return onDone(editing ? "Changes saved." : "Draft saved.", r);
    if (r.invited.length)
      return onDone(
        `Sent. ${r.invited[0]} was invited to your client portal.`,
        r,
      );
    if (r.failed.length && !r.emailed)
      return onDone(
        "Saved and marked as sent, but the email couldn't be delivered. Try Remind now later.",
        r,
      );
    return onDone("Sent to your client.", r);
  };

  const keepTemplate = async () => {
    const name = (templateName ?? "").trim();
    if (!name) return setError("Give the template a name.");
    const problem = requestFormProblem({ ...form, title: name }, true);
    if (problem) return setError(problem);
    const result = await saveTemplate({
      orgId,
      name,
      items: requestPayload(workId, form, false).items,
    });
    if ("error" in result)
      return setError(
        serverText(result.error, "The template couldn't be saved."),
      );
    setError(null);
    setTemplateName(null);
    setNotice(`Saved as template "${result.data.name}".`);
  };

  const templateOptions = useMemo(
    () => templates.data?.templates ?? [],
    [templates.data],
  );

  return (
    <PortalModal
      title={editing ? "Edit request list" : "New request list"}
      subtitle={
        recipients.data?.clientName
          ? `What you need from ${recipients.data.clientName}`
          : "What you need from your client"
      }
      onClose={onClose}
      width={680}
      footer={
        <>
          {templateName === null ? (
            <button
              type={"button"}
              className={`${modalCancel} mr-auto`}
              onClick={() => setTemplateName(title)}>
              Save as template
            </button>
          ) : (
            <span className={"mr-auto"} />
          )}
          <button type={"button"} className={modalCancel} onClick={onClose}>
            Cancel
          </button>
          {editing?.status !== "sent" && (
            <button
              type={"button"}
              className={modalCancel}
              disabled={busy}
              onClick={() => submit(false)}>
              Save draft
            </button>
          )}
          <button
            type={"button"}
            className={modalPrimary}
            disabled={busy}
            onClick={() => submit(!sentAlready)}>
            {busy ? "Saving…" : sentAlready ? "Save changes" : "Send to client"}
          </button>
        </>
      }>
      {templateName !== null && (
        <div
          className={
            "flex flex-wrap items-end gap-2 rounded-[12px] border border-[#D9D1FF] bg-[#F7F5FF] p-3"
          }>
          <label className={"flex min-w-[220px] flex-1 flex-col gap-1"}>
            <span className={label}>Template name</span>
            <input
              className={input}
              value={templateName}
              maxLength={100}
              autoFocus
              onChange={(e) => setTemplateName(e.target.value)}
            />
          </label>
          <button
            type={"button"}
            className={modalPrimary}
            disabled={templateState.isLoading}
            onClick={keepTemplate}>
            Save template
          </button>
          <button
            type={"button"}
            className={modalCancel}
            onClick={() => setTemplateName(null)}>
            Cancel
          </button>
        </div>
      )}

      <div className={"grid gap-3 sm:grid-cols-[1fr_180px]"}>
        <label className={"flex flex-col gap-1"}>
          <span className={label}>Title your client sees</span>
          <input
            className={input}
            value={title}
            maxLength={150}
            placeholder={"October documents"}
            onChange={(e) => setTitle(e.target.value)}
          />
        </label>
        <label className={"flex flex-col gap-1"}>
          <span className={label}>Due date</span>
          <input
            type={"date"}
            className={input}
            value={dueDate || ""}
            onChange={(e) => setDueDate(e.target.value)}
          />
        </label>
      </div>

      {!sentAlready && (
        <label className={"flex flex-col gap-1"}>
          <span className={label}>Message (optional)</span>
          <textarea
            className={`${input} h-auto min-h-[64px] py-2`}
            value={message}
            maxLength={2000}
            placeholder={
              "Hi Sarah, to finish your return we need a few things from you."
            }
            onChange={(e) => setMessage(e.target.value)}
          />
        </label>
      )}

      <div className={"flex flex-col gap-2"}>
        <div className={"flex flex-wrap items-center gap-2"}>
          <span className={"text-[14px] font-extrabold"}>
            Items ({items.length}/{MAX_ITEMS})
          </span>
          <span className={"flex-1"} />
          {suggestion.data && suggestion.data.items.length > 0 && (
            <button
              type={"button"}
              className={
                "text-[12.5px] font-bold text-[#6B4EFF] hover:text-[#4B2FC7]"
              }
              title={`From "${suggestion.data.fromTitle}"`}
              onClick={() =>
                fill(
                  suggestion.data!.items,
                  `Filled from last time ("${
                    suggestion.data!.fromTitle
                  }"). Check and change anything that's different this time.`,
                )
              }>
              ✨ Suggest from last time
            </button>
          )}
          {templateOptions.length > 0 && (
            <select
              className={`${input} h-8 w-auto py-0 text-[12.5px]`}
              value={""}
              aria-label={"Use a template"}
              onChange={(e) => {
                const t = templateOptions.find((x) => x.id === e.target.value);
                if (t) fill(t.items, `Added the items of "${t.name}".`);
              }}>
              <option value={""}>Use a template…</option>
              {templateOptions.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          )}
        </div>
        {notice && (
          <span
            className={
              "rounded-[10px] bg-[#F4F2FE] px-3 py-2 text-[12.5px] font-semibold text-[#4B2FC7]"
            }>
            {notice}
          </span>
        )}
        <ol className={"m-0 flex list-none flex-col gap-2 p-0"}>
          {items.map((item, i) => (
            <li
              key={item.id || `new-${i}`}
              className={
                "flex flex-col gap-2 rounded-[12px] border border-[#EDEFF3] bg-[#FBFBFD] p-3"
              }>
              <div className={"flex flex-wrap items-center gap-2"}>
                <span
                  className={
                    "w-6 text-center text-[12.5px] font-extrabold text-[#9097A8]"
                  }>
                  {i + 1}
                </span>
                <div
                  className={"flex rounded-[9px] border border-[#DCE2EA] p-0.5"}
                  role={"radiogroup"}
                  aria-label={`Item ${i + 1} type`}>
                  {ITEM_KINDS.map((k) => (
                    <button
                      key={k.key}
                      type={"button"}
                      role={"radio"}
                      aria-checked={item.kind === k.key}
                      title={k.hint}
                      onClick={() => setItem(i, { kind: k.key })}
                      className={`h-7 rounded-[7px] px-2.5 text-[12px] font-bold ${
                        item.kind === k.key
                          ? "bg-[#6B4EFF] text-white"
                          : "text-[#3A4256] hover:bg-[#F1F2F5]"
                      }`}>
                      {k.label}
                    </button>
                  ))}
                </div>
                <span className={"flex-1"} />
                <button
                  type={"button"}
                  className={iconBtn}
                  aria-label={"Move up"}
                  disabled={i === 0}
                  onClick={() => setItems((l) => moveItem(l, i, -1))}>
                  ↑
                </button>
                <button
                  type={"button"}
                  className={iconBtn}
                  aria-label={"Move down"}
                  disabled={i === items.length - 1}
                  onClick={() => setItems((l) => moveItem(l, i, 1))}>
                  ↓
                </button>
                <button
                  type={"button"}
                  className={iconBtn}
                  aria-label={"Delete item"}
                  onClick={() =>
                    setItems((l) => l.filter((_, index) => index !== i))
                  }>
                  ✕
                </button>
              </div>
              <input
                className={input}
                value={item.label}
                maxLength={300}
                aria-label={`Item ${i + 1}`}
                placeholder={
                  item.kind === "file"
                    ? "T4 slips from all employers"
                    : item.kind === "question"
                    ? "Did your address change this year?"
                    : "Did you sell any shares or crypto?"
                }
                onChange={(e) => setItem(i, { label: e.target.value })}
              />
              {item.kind === "yesno" && (
                <label
                  className={
                    "flex items-center gap-2 text-[12.5px] font-semibold text-[#3A4256]"
                  }>
                  <input
                    type={"checkbox"}
                    className={"accent-[#6B4EFF]"}
                    checked={item.fileIfYes}
                    onChange={(e) =>
                      setItem(i, { fileIfYes: e.target.checked })
                    }
                  />
                  If yes, ask them to upload a file
                </label>
              )}
            </li>
          ))}
        </ol>
        <div className={"flex flex-wrap gap-2"}>
          {ITEM_KINDS.map((k) => (
            <button
              key={k.key}
              type={"button"}
              className={smallBtn}
              disabled={items.length >= MAX_ITEMS}
              onClick={() => setItems((l) => [...l, newItem(k.key)])}>
              + {k.label}
            </button>
          ))}
        </div>
      </div>

      <div className={"flex flex-col gap-2"}>
        <span className={"text-[14px] font-extrabold"}>
          Reminders until complete
        </span>
        <div className={"grid gap-2 sm:grid-cols-4"}>
          {REMINDER_MODES.map((m) => (
            <button
              key={m.key}
              type={"button"}
              onClick={() => setReminderMode(m.key)}
              aria-pressed={reminderMode === m.key}
              className={`flex flex-col items-start gap-0.5 rounded-[12px] border-[1.5px] px-3 py-2 text-left ${
                reminderMode === m.key
                  ? "border-[#6B4EFF] bg-[#F7F5FF]"
                  : "border-[#DCE2EA] bg-white hover:bg-[#F7F8FA]"
              }`}>
              <span className={"text-[13px] font-extrabold"}>{m.label}</span>
              <span
                className={
                  "text-[11.5px] font-medium leading-snug text-[#6B7185]"
                }>
                {m.hint}
              </span>
            </button>
          ))}
        </div>
        {reminderMode === "custom" && (
          <label
            className={
              "flex items-center gap-2 text-[13px] font-semibold text-[#3A4256]"
            }>
            Every
            <input
              type={"number"}
              min={1}
              max={30}
              className={`${input} h-9 w-20`}
              value={everyDays}
              onChange={(e) => setEveryDays(Number(e.target.value))}
            />
            days
          </label>
        )}
        {reminderMode !== "off" && (
          <span className={"text-[12px] font-medium text-[#6B7185]"}>
            At most 6 automatic reminders by email, each listing only what's
            still missing. Then you'll get a notification.
          </span>
        )}
      </div>

      <div className={"flex flex-col gap-2"}>
        <label
          className={
            "flex items-start gap-2 text-[13px] font-semibold text-[#1F2433]"
          }>
          <input
            type={"checkbox"}
            className={"mt-0.5 accent-[#6B4EFF]"}
            checked={moveToReady}
            onChange={(e) => setMoveToReady(e.target.checked)}
          />
          When everything is received, move this work item to Ready and tell the
          assignee
        </label>
        <label
          className={
            "flex items-start gap-2 text-[13px] font-semibold text-[#1F2433]"
          }>
          <input
            type={"checkbox"}
            className={"mt-0.5 accent-[#6B4EFF]"}
            checked={repeat}
            onChange={(e) => setRepeat(e.target.checked)}
          />
          <span>
            Repeat with the work item
            <span className={"block text-[12px] font-medium text-[#6B7185]"}>
              When the next copy of this repeating work item is created, it gets
              this list as a draft for you to send with one click.
            </span>
          </span>
        </label>
      </div>

      {(who || recipientProblem) && (
        <div
          className={`rounded-[12px] px-3 py-2.5 text-[12.5px] font-semibold ${
            recipientProblem
              ? "bg-[#FFF4E5] text-[#8A4B00]"
              : "bg-[#F1F2F5] text-[#3A4256]"
          }`}>
          {recipientProblem || who}
        </div>
      )}
      {error && (
        <div
          role={"alert"}
          className={
            "rounded-[10px] bg-[#FDECEC] px-3 py-2 text-[13px] font-semibold text-[#B42318]"
          }>
          {error}
        </div>
      )}
    </PortalModal>
  );
};
